import { Card, CardContent } from "./ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { MoreVertical, CheckCircle2, Edit3, Trash2, EyeOff, HandHeart } from "lucide-react";
import { TagDisplay } from "./TagSelector";
import { Checkbox } from "./ui/checkbox";

function formatDateDefault(dateStr) {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function PrayerCard({
  prayer,
  onMarkAnswered,
  onEdit,
  onDelete,
  onPrayedFor,
  isAnswered = false,
  isHidden = false,
  canModify = true,
  showRequestor = false,
  formatDate = formatDateDefault,
  selectable = false,
  selected = false,
  onSelectToggle,
}) {
  const prayerCount = prayer.prayer_count || 0;
  return (
    <Card
      className={`border-stone card-hover ${isAnswered ? "bg-gold/5" : ""} ${isHidden ? "opacity-60" : ""} ${selected ? "ring-2 ring-sage/40" : ""}`}
      data-testid={`prayer-card-${prayer.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {selectable && (
            <div className="pt-1 shrink-0">
              <Checkbox
                checked={selected}
                onCheckedChange={() => onSelectToggle?.(prayer.id)}
                data-testid={`prayer-select-${prayer.id}`}
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-stone-dark leading-relaxed">{prayer.request}</p>

            <div className="mt-3 space-y-1">
              {prayer.requested_by && (
                <p className="text-sm text-stone-dark/60">
                  <span className="font-medium">Requested by:</span> {prayer.requested_by}
                </p>
              )}
              {prayer.notes && (
                <p className="text-sm text-stone-dark/60 italic">"{prayer.notes}"</p>
              )}
              {prayer.result && (
                <p className="text-sm text-sage">
                  <span className="font-medium">Result:</span> {prayer.result}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-stone-dark/50">
              {showRequestor && prayer.requestor_name && (
                <>
                  <span>By {prayer.requestor_name}</span>
                  <span>•</span>
                </>
              )}
              <span>Requested: {formatDate(prayer.date_requested || prayer.created_at)}</span>
              {isAnswered && (
                <span className="flex items-center gap-1 text-gold-dark">
                  <CheckCircle2 className="w-3 h-3" />
                  Answered: {formatDate(prayer.date_answered || prayer.answered_at)}
                </span>
              )}
              {isHidden && (
                <span className="flex items-center gap-1 text-stone-dark/40">
                  <EyeOff className="w-3 h-3" />
                  Hidden
                </span>
              )}
              {prayerCount >= 1 && (
                <span
                  className="flex items-center gap-1 text-sage"
                  data-testid={`prayer-count-${prayer.id}`}
                  title={`${prayerCount} ${prayerCount === 1 ? "person has" : "people have"} prayed for this`}
                >
                  <HandHeart className="w-3 h-3" />
                  {prayerCount}
                </span>
              )}
            </div>
            <TagDisplay tags={prayer.tags} />
          </div>

          {canModify && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" data-testid={`prayer-menu-${prayer.id}`}>
                  <MoreVertical className="w-4 h-4 text-stone-dark/50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!isAnswered && onPrayedFor && (
                  <DropdownMenuItem onClick={onPrayedFor} data-testid={`prayed-for-${prayer.id}`}>
                    <HandHeart className="w-4 h-4 mr-2 text-sage" />
                    Prayed for
                  </DropdownMenuItem>
                )}
                {!isAnswered && onMarkAnswered && (
                  <DropdownMenuItem onClick={onMarkAnswered} data-testid={`mark-answered-${prayer.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-2 text-gold-dark" />
                    Mark as Answered
                  </DropdownMenuItem>
                )}
                {onEdit && (
                  <DropdownMenuItem onClick={onEdit} data-testid={`edit-prayer-${prayer.id}`}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {onDelete && (
                  <DropdownMenuItem onClick={onDelete} className="text-red-600" data-testid={`delete-prayer-${prayer.id}`}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
